import { useMemo } from 'react';
import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { endpoints, withSideload } from '@rossum/api-client';
import { User } from '@rossum/api-client/users';
import { api } from '../../../../lib/apiClient';
import { neighborsKeys } from './queryKeys';

export type NeighborAnnotationEditor = {
  name: string;
  email?: string;
};

export type NeighborAnnotationMetadata = {
  fileName?: string;
  queueName?: string;
  workspaceName?: string;
  trainingEnabled: boolean;
  /** Deleted, purged or restricted annotations can't be toggled. */
  canToggleTraining: boolean;
  exportedAt: string | null;
  modifiedAt: string | null;
  modifier?: NeighborAnnotationEditor;
};

const toEditor = (user: User): NeighborAnnotationEditor => ({
  name:
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user.email ?? user.username),
  email: user.firstName && user.lastName ? user.email : undefined,
});

export const useNeighborAnnotationsMetadata = (annotationIds: number[]) => {
  const normalizedAnnotationIds = useMemo(
    () =>
      R.pipe(
        annotationIds,
        R.unique(),
        R.sort((a, b) => a - b)
      ),
    [annotationIds]
  );

  const query = useQuery({
    queryKey: neighborsKeys.annotationsMetadata(normalizedAnnotationIds),
    queryFn: async () => {
      const data = await api.request(
        withSideload(
          endpoints.annotations.list({
            id: normalizedAnnotationIds,
            pageSize: normalizedAnnotationIds.length,
          }),
          { documents: true, queues: true, workspaces: true, modifiers: true }
        )
      );

      const documentsByUrl = new Map(
        data.documents.map(document => [document.url, document])
      );
      const queuesByUrl = new Map(data.queues.map(queue => [queue.url, queue]));
      const workspacesByUrl = new Map(
        data.workspaces.map(workspace => [workspace.url, workspace])
      );
      const modifiersByUrl = new Map(
        data.modifiers.map(modifier => [modifier.url, modifier])
      );

      return new Map<number, NeighborAnnotationMetadata>(
        data.results.map(annotation => {
          const queue = annotation.queue
            ? queuesByUrl.get(annotation.queue)
            : undefined;
          const workspace = queue?.workspace
            ? workspacesByUrl.get(queue.workspace)
            : undefined;
          const modifier = annotation.modifier
            ? modifiersByUrl.get(annotation.modifier)
            : undefined;
          const isDeletedOrPurged =
            annotation.status === 'deleted' || annotation.status === 'purged';

          return [
            annotation.id,
            {
              fileName: annotation.document
                ? (documentsByUrl.get(annotation.document)?.originalFileName ??
                  undefined)
                : undefined,
              queueName: queue?.name,
              workspaceName: workspace?.name,
              trainingEnabled: isDeletedOrPurged
                ? false
                : (annotation.trainingEnabled ?? true),
              canToggleTraining:
                !isDeletedOrPurged && !annotation.restrictedAccess,
              exportedAt: annotation.exportedAt,
              modifiedAt: annotation.modifiedAt,
              modifier: modifier ? toEditor(modifier) : undefined,
            },
          ];
        })
      );
    },
    enabled: normalizedAnnotationIds.length > 0,
  });

  return {
    metadataByAnnotationId:
      query.data ?? new Map<number, NeighborAnnotationMetadata>(),
    isLoading: query.isLoading,
  };
};
